/**
 * Vail Cart Handler
 * Manages cart add-to-cart logic, cart state updates, and cart notice display.
 */
class VailCartHandler extends HTMLElement {
  /** @type {number | null} */
  #noticeTimeout = null;

  connectedCallback() {
    document.addEventListener('submit', this.#handleFormSubmit);
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    document.removeEventListener('submit', this.#handleFormSubmit);
    this.removeEventListener('click', this.#handleClick);
    if (this.#noticeTimeout) clearTimeout(this.#noticeTimeout);
  }

  /** Reset a submit button to its default state */
  #resetSubmitButton(btn) {
    if (!btn) return;
    btn.disabled = false;
    btn.removeAttribute('aria-busy');
    btn.classList.remove('is-loading', 'button--loading', 'loading', 'vail-cta-btn--loading');
  }

  /**
   * Fetch updated header-group and cart-drawer sections from Shopify,
   * then re-render them and dispatch cart update events.
   */
  #updateShopifyCartState() {
    const sections = ['header-group', 'cart-drawer'];

    fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({
        updates: {},
        sections: sections,
        sections_url: window.location.pathname,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        // Re-render header group (updates cart bubble)
        if (data.sections?.['header-group']) {
          const headerGroup = document.querySelector('#header-group');
          if (headerGroup) {
            headerGroup.innerHTML = data.sections['header-group'];
          }
        }

        // Re-render cart drawer
        if (data.sections?.['cart-drawer']) {
          const drawer = document.querySelector('cart-drawer');
          if (drawer) {
            drawer.outerHTML = data.sections['cart-drawer'];
          }
        }

        // Fallback: update item count elements directly
        if (typeof data.item_count !== 'undefined') {
          const count = data.item_count;

          document
            .querySelectorAll('[data-cart-count], .cart-count-bubble__count, .cart-count-bubble span')
            .forEach((el) => {
              el.textContent = count;
            });

          document.querySelectorAll('.cart-count-bubble, [data-cart-count-bubble]').forEach((el) => {
            if (count > 0) {
              el.classList.remove('hidden');
              el.removeAttribute('hidden');
            } else {
              el.classList.add('hidden');
            }
          });
        }

        // Dispatch cart events
        try {
          document.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: data,
                sourceId: 'theme-custom-cart',
                data: { itemCount: data.item_count },
              },
            })
          );
          document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart: data } }));
          document.dispatchEvent(new CustomEvent('vail:cart-updated', { detail: { cart: data } }));
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        } catch (_) {
          // Event dispatch failed; non-critical
        }
      })
      .catch(() => {
        // Cart update failed; state will resync on next interaction
      });
  }

  /** Show the cart notice bar with product info */
  #showCartNotice(item) {
    const bar = this.querySelector('[data-vail-cart-notice]');
    if (!bar) return;

    const titleEl = bar.querySelector('[data-vail-cart-notice-title]');
    const messageEl = bar.querySelector('[data-vail-cart-notice-message]');
    const thumbEl = bar.querySelector('[data-vail-cart-notice-thumb]');

    if (titleEl && item?.title) {
      titleEl.textContent = item.title;
    }

    if (messageEl) {
      messageEl.textContent = 'wurde in den Warenkorb gelegt.';
    }

    if (thumbEl) {
      const imageUrl = item?.image || item?.featured_image?.url || null;

      if (imageUrl) {
        thumbEl.src = imageUrl;
        thumbEl.alt = item.title || '';
        thumbEl.parentElement.style.display = '';
      } else {
        thumbEl.src = '';
        thumbEl.alt = '';
        thumbEl.parentElement.style.display = 'none';
      }
    }

    bar.hidden = false;
    bar.classList.add('vail-cart-notice--visible');
    if (this.#noticeTimeout) clearTimeout(this.#noticeTimeout);
    this.#noticeTimeout = setTimeout(() => {
      bar.classList.remove('vail-cart-notice--visible');
    }, 4000);
  }

  /** Handle click events (close button) */
  #handleClick = (event) => {
    const closeBtn = event.target.closest('[data-vail-cart-notice-close]');
    if (!closeBtn) return;
    const bar = this.querySelector('[data-vail-cart-notice]');
    if (bar) bar.classList.remove('vail-cart-notice--visible');
  };

  /** Intercept product form submissions for AJAX add-to-cart */
  #handleFormSubmit = (event) => {
    let form = event.target;
    if (!(form instanceof HTMLFormElement)) {
      form = event.target.closest('form');
    }
    if (!form) return;

    const isCartAddForm =
      (form.action && form.action.indexOf('/cart/add') !== -1) || !!form.closest('product-form');

    if (!isCartAddForm) return;

    // Skip forms already handled by product-form-component to avoid double submission
    if (form.closest('product-form-component')) return;

    event.preventDefault();

    const submitBtn = form.querySelector('button[type="submit"], button[name="add"], [type="submit"]');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.setAttribute('aria-busy', 'true');
    }

    const formData = new FormData(form);

    fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
      },
    })
      .then((response) => {
        if (!response.ok) {
          return response.json().then((data) => {
            throw data;
          });
        }
        return response.json();
      })
      .then((data) => {
        this.#showCartNotice(data);
        this.#resetSubmitButton(submitBtn);
        this.#updateShopifyCartState();
      })
      .catch((err) => {
        this.#resetSubmitButton(submitBtn);
        const msg = (err && (err.description || err.message)) || 'Der Artikel konnte nicht in den Warenkorb gelegt werden. Bitte versuchen Sie es erneut.';
        console.error('Cart add error:', err);
        alert(msg);
      });
  };
}

if (!customElements.get('vail-cart-handler')) {
  customElements.define('vail-cart-handler', VailCartHandler);
}
